import type { AffiliateCommissions } from "@decibeltrade/sdk";
import { Button } from "@decibeltrade/web-core/components";
import { useLocale, useTranslations } from "next-intl";
import { useState } from "react";
import { css } from "styled-system/css";
import { flex, grid, stack } from "styled-system/patterns";

import { useElectRewardsMode } from "@/contexts/mutations/elect-rewards-mode";
import { useAffiliateAccrualsQuery } from "@/contexts/queries/affiliate-accruals";
import { NumberFormatter } from "@/utils/number-formatter";
import { getServerTime, useServerTimeDelta } from "@/utils/server-time";

import { SectionCard, StatusTag, VinylBadge } from "../../components";
import { effectiveFromIso, formatElectionStart } from "../../election-date";
import { usdCompact } from "../../format";
import { toTierStanding } from "../../levels/tiers";

/**
 * Which of the two rewards an affiliate is actually earning.
 *
 * This card used to tag both modes ACTIVE and say the choice "unlocks with higher affiliate
 * levels". Neither was true once the election existed: an affiliate is on exactly one side, the
 * ledger stamps which one on every accrual row, and `/referrals/commissions` publishes it.
 * Tagging both told a points affiliate they were earning commission and a commission affiliate
 * that their referral Amps were still running.
 *
 * There is no button here yet because the write path does not exist. It is a small route rather
 * than a subsystem: the wallet already signed in through SIWA
 * (`server/src/middleware/signature-auth.ts`), so the switch is one more write behind that
 * session — taking the address from the session, never from the body, or anyone could switch
 * anyone. It writes one row to `affiliate_reward_mode` with `set_by` recording who asked, and
 * both the accrual cron and the referral-Amps step already resolve that table per day.
 *
 * The affiliate-portal fork records elections differently, as wallet-signed intents carrying a
 * nonce and signature. That buys provable consent in a dispute, which a session cookie does not,
 * and it is one of the places the two designs still disagree.
 */
export const RewardsModeCard = ({
  account,
  networkFeesUsd,
}: {
  account: string | undefined;
  /** Fee revenue the network generated, for the switch pitch. */
  networkFeesUsd?: number;
}) => {
  const t = useTranslations("affiliates.rewardsMode");
  const tStatus = useTranslations("affiliates.status");
  const locale = useLocale();
  const accruals = useAffiliateAccrualsQuery({ affiliateAccount: account });

  // Points until the server says otherwise. It is the status quo, the mode every affiliate
  // starts on, and the safe direction to be wrong in: claiming commission for someone whose
  // mode has not loaded would show them earnings they may not be entitled to.
  const isCommission = accruals.data?.mode === "usd";
  // An election that has not arrived yet. It cannot be expressed by `mode`, which only ever
  // describes today, and it is the difference between "your switch worked" and a screen that looks
  // like nothing happened.
  const pendingFrom = accruals.data?.pending_mode ? accruals.data.pending_effective_from : "";

  return (
    <SectionCard label={t("cardLabel")}>
      <div className={stack({ gap: "16" })}>
        <Mode
          name={t("pointsName")}
          description={t("pointsDescription")}
          activeLabel={isCommission ? undefined : tStatus("active")}
        />
        <Mode
          name={t("commissionName")}
          description={t("commissionDescription")}
          activeLabel={isCommission ? tStatus("active") : undefined}
        />
        {/* The inactive mode carries no tag at all — not "available". The choice is one-time, so
            offering Points back to an affiliate already on Commission would be a promise the
            ledger will not keep. */}
        <p className={noteStyles}>
          {isCommission ? t("electionNoteCommission") : t("electionNotePoints")}
        </p>

        {/* Three states, and the middle one is the one that was missing. Offered only while the
            affiliate is on points AND has nothing pending: the allowance is spent the moment the
            row exists, so a second click can only produce an error. */}
        {pendingFrom ? (
          <div className={pendingStyles}>
            <div className={flex({ gap: "8", alignItems: "center", flexWrap: "wrap" })}>
              <span className={css({ textStyle: "highlight.md", color: "brandYellow.550" })}>
                {t("switchRecorded")}
              </span>
              <StatusTag variant="lime" label={tStatus("upcoming")} />
            </div>
            {/* The affiliate's own clock, not the ledger's. `pendingFrom` is a UTC day, and west
                of Greenwich it begins the evening before — printing it raw told a Bogotá affiliate
                a date their calendar had not reached yet. */}
            <p className={noteStyles}>
              {t("pendingSwitch", { date: formatElectionStart(pendingFrom, locale) })}
            </p>
            {/* The block said only when Commission starts, which is the good news. What it left
                out is that the affiliate has just spent something they cannot get back — and this
                is the last screen where saying so still matters, because after the date passes
                there is nothing to warn about. Amber against the lime tint on purpose: the tag
                confirms it worked, this line says it is permanent, and those are different
                things. */}
            <p className={permanentStyles}>{t("pendingIrreversible")}</p>
          </div>
        ) : // The election is one-time and irreversible, so it is not offered until the mode is
        // known. `isCommission` is false while the request is in flight, which showed the
        // switch to an affiliate already on USD — one confirmation away from spending an
        // allowance they had already used.
        accruals.data === undefined || isCommission ? null : (
          <SwitchPitch data={accruals.data} networkFeesUsd={networkFeesUsd} />
        )}
      </div>
    </SectionCard>
  );
};

/** The fee basis the pitch figure covers, matching the Commissions cell and page. */
const BASIS_WINDOW_DAYS = 30;

// The Commission pitch above the switch: the tier/rates and the network's fees it would pay.
const SwitchPitch = ({
  data,
  networkFeesUsd,
}: {
  data: AffiliateCommissions;
  networkFeesUsd: number | undefined;
}) => {
  const t = useTranslations("affiliates.rewardsMode");
  const tTierNames = useTranslations("affiliates.tierNames");
  // No standing (empty ladder) drops the level tile; the switch still renders.
  const standing = toTierStanding(data);
  const rung = data.tiers.find((r) => r.tier === data.current_tier);
  const hasFees = networkFeesUsd !== undefined;

  return (
    <div className={stack({ gap: "12" })}>
      {(standing || hasFees) && (
        <>
          <div className={pitchLabelStyles}>{t("pitchLabel")}</div>
          <div className={pitchGridStyles}>
            {standing ? (
              <div className={pitchTileStyles}>
                <VinylBadge tier={standing.tier} size={64} />
                <div className={stack({ gap: "4", minW: "0" })}>
                  <div className={pitchTierNameStyles}>{tTierNames(standing.tier)}</div>
                  {rung ? (
                    <div className={css({ textStyle: "body.sm", color: "text.secondary" })}>
                      {t("pitchRates", { rate: pct(rung.rate_l1), subRate: pct(rung.rate_l2) })}
                    </div>
                  ) : null}
                  {standing.next ? (
                    <div className={css({ textStyle: "body.sm", color: "text.tertiary" })}>
                      {t("pitchToNextTier", {
                        amount: usdCompact(standing.next.nuvNeededUsd),
                        tier: tTierNames(standing.next.tier),
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
            {hasFees ? (
              <div className={pitchTileStyles}>
                <div className={stack({ gap: "4", minW: "0" })}>
                  <div className={pitchValueStyles}>{NumberFormatter.usdc(networkFeesUsd)}</div>
                  <div className={css({ textStyle: "body.sm", color: "text.secondary" })}>
                    {t("pitchNetworkFeesLabel")}
                  </div>
                  <div className={css({ textStyle: "body.sm", color: "text.tertiary" })}>
                    {t("pitchNetworkFeesWindow", { days: BASIS_WINDOW_DAYS })}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </>
      )}
      <SwitchToCommission />
    </div>
  );
};

/** A rate as a whole percent, keeping a half point when there is one (silver's L2 is 7.5%). */
function pct(rate: number): string {
  const p = rate * 100;
  return Number.isInteger(p) ? String(p) : p.toFixed(1);
}

/**
 * The switch, behind a confirmation.
 *
 * Not ceremony: one click decides how this affiliate is paid from the next day onwards, it cannot
 * be undone, and the dialog is the only place they are told what stops and when it starts.
 */
const SwitchToCommission = () => {
  const t = useTranslations("affiliates.rewardsMode");
  const locale = useLocale();
  const [isConfirming, setIsConfirming] = useState(false);
  const elect = useElectRewardsMode();
  // The date is only a preview — the server records its own and returns it. `useServerTimeDelta`
  // so the reading settles once the real delta lands, rather than off a clock that may be wrong.
  useServerTimeDelta();
  // Shown as the moment it happens in the affiliate's own zone. The UTC day underneath is what the
  // server records; it is not what someone reading this at 6:55pm in Bogotá needs to see.
  const effectiveDate = formatElectionStart(effectiveFromIso(new Date(getServerTime())), locale);

  if (!isConfirming) {
    return (
      <div>
        {/* `primary`, the same weight the portal gives claiming money and connecting a wallet.
            It was `secondary` — `background.tertiary` on this card's surface, which reads as
            disabled rather than inviting. Nothing is lost by making it loud: this button and the
            confirmation's own primary never appear together, since opening the confirmation
            replaces it. */}
        <Button variant="primary" size="md" onClick={() => setIsConfirming(true)}>
          {t("switchCta")}
        </Button>
      </div>
    );
  }

  return (
    <div className={confirmStyles}>
      <div className={css({ textStyle: "highlight.md", color: "amber.500" })}>
        {t("confirmTitle")}
      </div>
      <p className={noteStyles}>{t("confirmBody", { date: effectiveDate })}</p>
      {/* Not `noteStyles`. This was tertiary body text sitting under the body copy, which is where
          the eye goes last — the one sentence that says the decision is permanent was the quietest
          thing in the dialog. It carries the weight of the heading now, because it is the only
          line here an affiliate cannot afford to skim. */}
      <p className={permanentStyles}>{t("confirmOnce")}</p>
      {elect.error ? (
        <p className={css({ textStyle: "body.md", color: "text.negative" })}>
          {t("switchFailed", { reason: elect.error.message })}
        </p>
      ) : null}
      <div className={flex({ gap: "8", flexWrap: "wrap" })}>
        <Button
          variant="primary"
          size="md"
          onClick={() => void elect.mutateAsync("usd")}
          disabled={elect.isPending}
        >
          {t("confirmCta")}
        </Button>
        <Button
          variant="text"
          size="md"
          onClick={() => setIsConfirming(false)}
          disabled={elect.isPending}
        >
          {t("confirmCancel")}
        </Button>
      </div>
    </div>
  );
};

const Mode = ({
  name,
  description,
  activeLabel,
}: {
  name: string;
  description: string;
  activeLabel: string | undefined;
}) => (
  <div className={stack({ gap: "8" })}>
    <div className={flex({ gap: "8", alignItems: "center", flexWrap: "wrap" })}>
      <span className={modeNameStyles}>{name}</span>
      {activeLabel === undefined ? null : <StatusTag variant="lime" label={activeLabel} />}
    </div>
    <p className={copyStyles}>{description}</p>
  </div>
);

const modeNameStyles = css({
  textStyle: "heading.xs",
  fontWeight: "medium",
  color: "text.primary",
});

const copyStyles = css({
  textStyle: "body.md",
  color: "text.secondary",
});

const noteStyles = css({
  textStyle: "body.md",
  color: "text.tertiary",
});

/**
 * The one line that says a decision cannot be undone.
 *
 * Amber and at highlight weight in both places it appears — the confirmation and the recorded
 * state — so it reads the same before and after. An affiliate who skimmed it on the way in should
 * meet the identical sentence on the way out, not a softer version of it.
 */
const permanentStyles = css({
  textStyle: "highlight.md",
  color: "amber.500",
});

/**
 * Amber, because this is a warning and not a form: the affiliate is one click from a decision with
 * no undo. Tinted surface plus a left rule rather than a full border — the card already sits
 * inside a bordered section, and a second box reads as a modal that never opened.
 */
const confirmStyles = stack({
  gap: "8",
  p: "12",
  bg: "[rgba(255, 190, 11, 0.10)]",
  borderLeft: "[2px solid token(colors.amber.500)]",
  rounded: "300",
});

/**
 * Lime, the brand accent the portal already uses for an active state — not the semantic green.
 * This is "your choice is on the books", which is brand state rather than a positive outcome, and
 * it has to read as settled at a glance: a grey block is what the affiliate mistook for nothing
 * having happened.
 */
const pendingStyles = stack({
  gap: "8",
  p: "12",
  bg: "[rgba(204, 255, 0, 0.10)]",
  borderLeft: "[2px solid token(colors.brandYellow.550)]",
  rounded: "300",
});

const pitchLabelStyles = css({
  textStyle: "highlight.sm",
  letterSpacing: "[1px]",
  textTransform: "uppercase",
  color: "text.tertiary",
});

// Two organised tiles — the level, and the fee figure — side by side, stacked on narrow.
const pitchGridStyles = grid({ columns: { base: 1, md: 2 }, gap: "8" });

const pitchTileStyles = flex({
  gap: "12",
  alignItems: "center",
  p: "12",
  bg: "background.secondary",
  rounded: "300",
  minW: "0",
});

const pitchTierNameStyles = css({
  textStyle: "heading.xs",
  fontWeight: "medium",
  color: "text.primary",
});

const pitchValueStyles = css({
  textStyle: "highlight.lg",
  fontWeight: "medium",
  color: "text.primary",
});
